import { tKey } from 'ember-intl';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import {
  KPI_CATEGORIES as BI_KPI_CATEGORIES,
  type KpiCategory,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import { KPI_CATEGORIES } from 'lumen/utils/constants/ri-constant-values';

export const PMS_MAPPING_TABS = {
  SEGMENT: RI_BREAKDOWNS.SEGMENT,
  RATE_CODE: RI_BREAKDOWNS.RATE_CODE,
  TRAVEL_AGENT: RI_BREAKDOWNS.TRAVEL_AGENT,
  COMPANY: RI_BREAKDOWNS.COMPANY,
  ROOM_TYPE: RI_BREAKDOWNS.ROOM_TYPE,
  MARKET_CODE: 'market_code',
  WEEKDAY: RI_BREAKDOWNS.WEEKDAY,
  SOURCE: RI_BREAKDOWNS.SOURCE,
  ORIGIN: RI_BREAKDOWNS.ORIGIN,
  GUEST_COUNTRY: RI_BREAKDOWNS.GUEST_COUNTRY,
} as const;
export type PmsMappingTab =
  (typeof PMS_MAPPING_TABS)[keyof typeof PMS_MAPPING_TABS];
export const isPmsMappingTab = (value: string): value is PmsMappingTab =>
  Object.values(PMS_MAPPING_TABS).includes(value as PmsMappingTab);

export const PMS_MAPPING_TABS_BENCHMARK = [
  PMS_MAPPING_TABS.MARKET_CODE,
  PMS_MAPPING_TABS.RATE_CODE,
];

export const PMS_MAPPING_TABS_TRANSLATION_KEY_MAPPING = {
  [PMS_MAPPING_TABS.SEGMENT]: tKey('settings.pms_mapping.segementation'),
  [PMS_MAPPING_TABS.RATE_CODE]: tKey('revenue_insight.rate_code'),
  [PMS_MAPPING_TABS.TRAVEL_AGENT]: tKey('revenue_insight.travel_agent'),
  [PMS_MAPPING_TABS.COMPANY]: tKey('revenue_insight.company'),
  [PMS_MAPPING_TABS.ROOM_TYPE]: tKey('revenue_insight.room_type'),
  [PMS_MAPPING_TABS.MARKET_CODE]: tKey('benchmark_insight.market_code'),
  [PMS_MAPPING_TABS.WEEKDAY]: 'revenue_insight.weekday',
  [PMS_MAPPING_TABS.SOURCE]: tKey('revenue_insight.source'),
  [PMS_MAPPING_TABS.ORIGIN]: tKey('revenue_insight.origin'),
  [PMS_MAPPING_TABS.GUEST_COUNTRY]: tKey('revenue_insight.guest_country'),
};

export const BENI_PMS_MAPPING_TABS_TRANSLATION_KEY_MAPPING = {
  [PMS_MAPPING_TABS.RATE_CODE]: tKey('benchmark_insight.rate_code'),
  [PMS_MAPPING_TABS.MARKET_CODE]: tKey('benchmark_insight.market_code'),
};

export const PMS_MAPPING_SEGMENT_TRANSLATIONS: Partial<
  Record<KpiCategory, string>
> = {
  [KPI_CATEGORIES.TRANSIENT]: tKey('revenue_insight.transient'),
  [KPI_CATEGORIES.GROUP]: tKey('revenue_insight.group'),
  [KPI_CATEGORIES.CONTRACTED_CREW]: tKey(
    'revenue_insight.categories.travel_agent.contract_crew',
  ),
  [KPI_CATEGORIES.OTHER]: tKey('general.other'),
};

export const PMS_MAPPING_TRAVEL_AGENT_CATEGORIES = {
  ENTERTAINMENT_AGENT: 'entertainment_agent',
  METASEARCH_ENGINE: 'metasearch_engine',
  WHOLESALER: 'wholesaler',
  ASSOCIATION_PLANNER: 'association_planner',
  CREW: 'crew',
  ECOMMERCE_MEDIA_AGENT: 'ecommerce_media_agent',
  HIGHSTREET_TRAVEL_AGENT: 'highstreet_travel_agent',
  EXTENDED_STAY_AGENT: 'extended_stay_agent',
  ONLINE_TRAVEL_AGENT: 'online_travel_agent',
  TOUR_OPERATOR: 'tour_operator',
  BUSINESS_TRAVEL_AGENT: 'business_travel_agent',
  MICE_AGENT: 'mice_agent',
  RELOCATION_AGENT: 'relocation_agent',
  OTHER: 'other',
} as const;
export type PmsMappingTravelAgentCategory =
  (typeof PMS_MAPPING_TRAVEL_AGENT_CATEGORIES)[keyof typeof PMS_MAPPING_TRAVEL_AGENT_CATEGORIES];
export const isPmsMappingTravelAgentCategory = (
  value: string,
): value is PmsMappingTravelAgentCategory =>
  Object.values(PMS_MAPPING_TRAVEL_AGENT_CATEGORIES).includes(
    value as PmsMappingTravelAgentCategory,
  );

export const PMS_MAPPING_TRAVEL_AGENT_TRANSLATIONS: Record<
  PmsMappingTravelAgentCategory,
  string
> = {
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.ENTERTAINMENT_AGENT]: tKey(
    'revenue_insight.categories.travel_agent.entertainment_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.METASEARCH_ENGINE]: tKey(
    'revenue_insight.categories.travel_agent.metasearch_engines',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.WHOLESALER]: tKey(
    'revenue_insight.categories.travel_agent.wholesalers',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.ASSOCIATION_PLANNER]: tKey(
    'revenue_insight.categories.travel_agent.association_planner',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.CREW]: tKey(
    'revenue_insight.categories.travel_agent.contract_crew',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.ECOMMERCE_MEDIA_AGENT]: tKey(
    'revenue_insight.categories.travel_agent.ecommerce_media_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.HIGHSTREET_TRAVEL_AGENT]: tKey(
    'revenue_insight.categories.travel_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.EXTENDED_STAY_AGENT]: tKey(
    'revenue_insight.categories.travel_agent.extended_stay_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.ONLINE_TRAVEL_AGENT]: tKey(
    'revenue_insight.categories.travel_agent.online_travel_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.TOUR_OPERATOR]: tKey(
    'revenue_insight.categories.travel_agent.tour_operators',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.BUSINESS_TRAVEL_AGENT]: tKey(
    'revenue_insight.categories.travel_agent.business_travel_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.MICE_AGENT]: tKey(
    'revenue_insight.categories.travel_agent.mice_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.RELOCATION_AGENT]: tKey(
    'revenue_insight.categories.travel_agent.relocation_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.OTHER]: tKey('general.other'),
};

export const PMS_MAPPING_TRAVEL_AGENT_INFO_TOOLTIP_TRANSLATIONS: Partial<
  Record<PmsMappingTravelAgentCategory, string>
> = {
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.ENTERTAINMENT_AGENT]: tKey(
    'settings.pms_mapping.tooltip.entertainment_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.METASEARCH_ENGINE]: tKey(
    'settings.pms_mapping.tooltip.metasearch_engines',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.WHOLESALER]: tKey(
    'settings.pms_mapping.tooltip.wholesalers',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.ASSOCIATION_PLANNER]: tKey(
    'settings.pms_mapping.tooltip.assocation_planner',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.CREW]: tKey(
    'settings.pms_mapping.tooltip.crew',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.ECOMMERCE_MEDIA_AGENT]: tKey(
    'settings.pms_mapping.tooltip.ecommerce_media_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.HIGHSTREET_TRAVEL_AGENT]: tKey(
    'settings.pms_mapping.tooltip.highstreet_travel_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.EXTENDED_STAY_AGENT]: tKey(
    'settings.pms_mapping.tooltip.extended_stay_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.ONLINE_TRAVEL_AGENT]: tKey(
    'settings.pms_mapping.tooltip.online_travel_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.TOUR_OPERATOR]: tKey(
    'settings.pms_mapping.tooltip.tour_operators',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.BUSINESS_TRAVEL_AGENT]: tKey(
    'settings.pms_mapping.tooltip.business_travel_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.MICE_AGENT]: tKey(
    'settings.pms_mapping.tooltip.mice_agent',
  ),
  [PMS_MAPPING_TRAVEL_AGENT_CATEGORIES.RELOCATION_AGENT]: tKey(
    'settings.pms_mapping.tooltip.relocation_agent',
  ),
};

export const PMS_MAPPING_WEEKDAY_CATEGORIES = {
  WEEKDAY: 'weekday',
  WEEKEND: 'weekend',
  OTHER: 'other',
} as const;
export type PmsMappingWeekdayCategory =
  (typeof PMS_MAPPING_WEEKDAY_CATEGORIES)[keyof typeof PMS_MAPPING_WEEKDAY_CATEGORIES];
export const isPmsMappingWeekdayCategory = (
  value: string,
): value is PmsMappingWeekdayCategory =>
  Object.values(PMS_MAPPING_WEEKDAY_CATEGORIES).includes(
    value as PmsMappingWeekdayCategory,
  );

export const PMS_MAPPING_WEEKDAY_TRANSLATIONS: Record<
  PmsMappingWeekdayCategory,
  string
> = {
  [PMS_MAPPING_WEEKDAY_CATEGORIES.WEEKDAY]: tKey('week.weekday'),
  [PMS_MAPPING_WEEKDAY_CATEGORIES.WEEKEND]: tKey('week.weekend'),
  [PMS_MAPPING_WEEKDAY_CATEGORIES.OTHER]: tKey('general.other'),
};

export const BENI_PMS_MAPPING_FIXED_CATEGORY_TRANSLATIONS: Record<
  KpiCategory,
  string
> = {
  [BI_KPI_CATEGORIES.TRANSIENT]: tKey('benchmark_insight.category.transient'),
  [BI_KPI_CATEGORIES.RETAIL]: tKey('benchmark_insight.category.retail'),
  [BI_KPI_CATEGORIES.DIRECT]: tKey('benchmark_insight.category.direct'),
  [BI_KPI_CATEGORIES.NON_DIRECT]: tKey('benchmark_insight.category.non_direct'),
  [BI_KPI_CATEGORIES.DISCOUNT]: tKey('benchmark_insight.category.discount'),
  [BI_KPI_CATEGORIES.GENERAL_DISCOUNT]: tKey(
    'benchmark_insight.category.general_discount',
  ),
  [BI_KPI_CATEGORIES.PROMOTION]: tKey('benchmark_insight.category.promotion'),
  [BI_KPI_CATEGORIES.PACKAGE_PATH_OPAQUE]: tKey(
    'benchmark_insight.category.package_path_opaque',
  ),
  [BI_KPI_CATEGORIES.NEGOTIATED]: tKey('benchmark_insight.category.negotiated'),
  [BI_KPI_CATEGORIES.CORPORATE_DISCOUNT]: tKey(
    'benchmark_insight.category.corporate_discount',
  ),
  [BI_KPI_CATEGORIES.CORPORATE_NEGOTIATED]: tKey(
    'benchmark_insight.category.corporate_negotiated',
  ),
  [BI_KPI_CATEGORIES.GOVERNMENT]: tKey('benchmark_insight.category.government'),
  [BI_KPI_CATEGORIES.WHOLESALE]: tKey('benchmark_insight.category.wholesale'),
  [BI_KPI_CATEGORIES.QUALIFIED]: tKey('benchmark_insight.category.qualified'),
  [BI_KPI_CATEGORIES.GROUP]: tKey('benchmark_insight.category.group'),
  [BI_KPI_CATEGORIES.BUSINESS]: tKey('benchmark_insight.category.business'),
  [BI_KPI_CATEGORIES.LEISURE]: tKey('benchmark_insight.category.leisure'),
  [BI_KPI_CATEGORIES.CONTRACTED_CREW]: tKey(
    'benchmark_insight.category.contracted_crew',
  ),
  [BI_KPI_CATEGORIES.OTHER]: tKey('benchmark_insight.category.other'),
};

export const PMS_MAPPING_HIERARCHY_ACTIONS = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};

export const KPI_DISPLAY_DEFAULT_MAPPING = 'default_mapping';

export const KPI_DISPLAY_CUSTOM_MAPPING = 'custom_mapping';

export type KpiDisplay =
  | typeof KPI_DISPLAY_DEFAULT_MAPPING
  | typeof KPI_DISPLAY_CUSTOM_MAPPING;

export const MAPPING_TYPE_RATE_CODE = 'rate_code';
export const MAPPING_TYPE_MARKET_CODE = 'market_segment';

export type MappingType =
  | typeof MAPPING_TYPE_RATE_CODE
  | typeof MAPPING_TYPE_MARKET_CODE;

// Max amount of characters category and subcategory names can have
export const RI_MAPPING_MAX_LENGTH_NAME = 50;

export const SELECT_ALL_CHECKBOX_STATES = {
  CHECKED: 'checked',
  DISABLED: 'disabled',
  UNCHECKED: 'unchecked',
};

export const MAPPING_URL = {
  REVENUE: '/apigateway/v1/app/revenueinsight/mapping',
  BENCHMARK: '/api/v3/benchmarkinsight/mapping',
};

export const PMS_MAPPING_SETTINGS_UNMAPPED = 'settings.pms_mapping.unmapped';
export const DISMISSED_SUGGESTIONS_LOCALSTORAGE_KEY = 'dismissedSuggestions';

export const PMS_MAPPING_CATEGORY_FILTERS = {
  MAPPED: 'mapped',
  UNMAPPED: 'unmapped',
  SUGGESTED_MAPPING: 'suggested_mapping',
  UNSAVED_CHANGES: 'unsaved_changes',
  ALL: 'all',
};
